import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmDialogHost } from "@/components/ConfirmDialog";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { requiresAuth } from "@/config/toolAccess";
import { lazy, Suspense } from "react";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import NotFoundPage from "@/pages/NotFoundPage";
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage"));

// Lazy-loaded pages — only downloaded when visited
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const LandingPage = lazy(() => import("@/pages/marketing/LandingPage"));
const FeaturesPage = lazy(() => import("@/pages/marketing/FeaturesPage"));
const FlagshipToolPage = lazy(() => import("@/pages/marketing/FlagshipToolPage"));
const BlogIndex = lazy(() => import("@/pages/marketing/BlogPage"));
const BlogPost = lazy(() => import("@/pages/marketing/BlogPage").then((m) => ({ default: m.BlogPost })));
const ToolCategoryPage = lazy(() => import("@/pages/ToolCategoryPage"));
const ResourcesPage = lazy(() => import("@/pages/ResourcesPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const ArticleEditor = lazy(() => import("@/pages/admin/ArticleEditor"));
const KeywordDensityTool = lazy(() => import("@/pages/tools/KeywordDensityTool"));
const MetaAnalyzerTool = lazy(() => import("@/pages/tools/MetaAnalyzerTool"));
const SitemapValidatorTool = lazy(() => import("@/pages/tools/SitemapValidatorTool"));
const RobotsCheckerTool = lazy(() => import("@/pages/tools/RobotsCheckerTool"));
const ContentGeneratorTool = lazy(() => import("@/pages/tools/ContentGeneratorTool"));
const ContentAnalyzerTool = lazy(() => import("@/pages/tools/ContentAnalyzerTool"));
const ScreamingFrogAnalyzerTool = lazy(() => import("@/pages/tools/ScreamingFrogAnalyzerTool"));
const RankTrackerTool = lazy(() => import("@/pages/tools/RankTrackerTool"));
const SchemaValidatorTool = lazy(() => import("@/pages/tools/SchemaValidatorTool"));
const SchemaGeneratorTool = lazy(() => import("@/pages/tools/SchemaGeneratorTool"));
const InternalLinkingTool = lazy(() => import("@/pages/tools/InternalLinkingTool"));
const KeywordMappingTool = lazy(() => import("@/pages/tools/KeywordMappingTool"));
const KeywordStrategyTool = lazy(() => import("@/pages/tools/KeywordStrategyTool"));
const ContentBriefTool = lazy(() => import("@/pages/tools/ContentBriefTool"));
const ImageOptimizerTool = lazy(() => import("@/pages/tools/ImageOptimizerTool"));
const BrandedContentTool = lazy(() => import("@/pages/tools/BrandedContentTool"));
const CategoryContentTool = lazy(() => import("@/pages/tools/CategoryContentTool"));
const PLPKeywordTool = lazy(() => import("@/pages/tools/PLPKeywordTool"));
const TitleTagOptimizer = lazy(() => import("@/pages/tools/TitleTagOptimizer"));
const HeadingAnalyzerTool = lazy(() => import("@/pages/tools/HeadingAnalyzerTool"));
const IndexCheckerTool = lazy(() => import("@/pages/tools/IndexCheckerTool"));
const SchemaRecommenderTool = lazy(() => import("@/pages/tools/SchemaRecommenderTool"));
const SchemaAuditDashboard = lazy(() => import("@/pages/tools/SchemaAuditDashboard"));
const VoiceProfileTool = lazy(() => import("@/pages/tools/VoiceProfileTool"));
const ContentHubPlanner = lazy(() => import("@/pages/tools/ContentHubPlanner"));
const SerpSimilarityTool = lazy(() => import("@/pages/tools/SerpSimilarityTool"));
const QueryTopicsPage = lazy(() => import("@/pages/tools/QueryTopicsPage"));
const CompetitorContentGapTool = lazy(() => import("@/pages/tools/CompetitorContentGapTool"));
const KeywordClusteringTool = lazy(() => import("@/pages/tools/KeywordClusteringTool"));
const KeywordCannibalizationFinder = lazy(() => import("@/pages/tools/KeywordCannibalizationFinder"));
const PageSeoAnalyzer = lazy(() => import("@/pages/tools/PageSeoAnalyzer"));
const AuditIndexListPage = lazy(() => import("@/pages/tools/AuditIndexListPage"));
const AuditIndexProjectPage = lazy(() => import("@/pages/tools/AuditIndexProjectPage"));
const DataInsightsPage = lazy(() => import("@/pages/tools/DataInsightsPage"));
const ContentAuditTool = lazy(() => import("@/pages/tools/ContentAuditTool"));
const PageTypeAssessment = lazy(() => import("@/pages/tools/PageTypeAssessment"));
const KeywordPositionChange = lazy(() => import("@/pages/tools/KeywordPositionChange"));
const EntitySeoTool = lazy(() => import("@/pages/tools/EntitySeoTool"));
const KeywordResearchWorkflow = lazy(() => import("@/pages/tools/KeywordResearchWorkflow"));
const ContentGapTool = lazy(() => import("@/pages/tools/ContentGapTool"));
const KeywordGapAnalysisTool = lazy(() => import("@/pages/tools/KeywordGapAnalysisTool"));
const BacklinkGapTool = lazy(() => import("@/pages/tools/BacklinkGapTool"));
const PAAClusterTool = lazy(() => import("@/pages/tools/PAAClusterTool"));
const RecipeScraperTool = lazy(() => import("@/pages/tools/RecipeScraperTool"));
const SEODirectorTool = lazy(() => import("@/pages/tools/SEODirectorTool"));
const KeywordResearchAgentTool = lazy(() => import("@/pages/tools/KeywordResearchAgentTool"));
const LandingPageAuditorTool = lazy(() => import("@/pages/tools/LandingPageAuditorTool"));
const WebsiteDataCollectorTool = lazy(() => import("@/pages/tools/WebsiteDataCollectorTool"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const BillingSuccessPage = lazy(() => import("@/pages/BillingSuccessPage"));
const RankTrackerDashboard = lazy(() => import("@/pages/RankTrackerDashboard"));
const GSCDashboard = lazy(() => import("@/pages/GSCDashboard"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ResultsHistoryPage = lazy(() => import("@/pages/ResultsHistoryPage"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const CustomToolsPage = lazy(() => import("@/pages/CustomToolsPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const ContentMachinePage = lazy(() => import("@/pages/ContentMachinePage"));
const RecipePrioritizerPage = lazy(() => import("@/pages/RecipePrioritizerPage"));
const ProductionPipelinePage = lazy(() => import("@/pages/ProductionPipelinePage"));
const DocPage = lazy(() => import("@/pages/DocPage"));
const KeywordsPage = lazy(() => import("@/pages/pipeline/KeywordsPage"));
const EditorialQAPage = lazy(() => import("@/pages/pipeline/EditorialQAPage"));
const ContentPage = lazy(() => import("@/pages/pipeline/ContentPage"));
const RecipeDetailPage = lazy(() => import("@/pages/pipeline/RecipeDetailPage"));
const ImagesPage = lazy(() => import("@/pages/pipeline/ImagesPage"));
const PublishPage = lazy(() => import("@/pages/pipeline/PublishPage"));
const SchedulePage = lazy(() => import("@/pages/pipeline/SchedulePage"));
const BacklogPage = lazy(() => import("@/pages/pipeline/BacklogPage"));
const LearnedPhrasesPage = lazy(() => import("@/pages/pipeline/LearnedPhrasesPage"));
const KeywordRulesPage = lazy(() => import("@/pages/KeywordRulesPage"));
const ServicesProposalPage = lazy(() => import("@/pages/ServicesProposalPage"));
const ProposalBuilderPage = lazy(() => import("@/pages/ProposalBuilderPage"));
const SharedReportPage = lazy(() => import("@/pages/tools/SharedReportPage"));
const UniversalSharedReport = lazy(() => import("@/pages/tools/UniversalSharedReport"));
const SharedRankTrackerPage = lazy(() => import("@/pages/tools/SharedRankTrackerPage"));
const PageOptimizer = lazy(() => import("@/pages/tools/PageOptimizer"));
const PageOptimizerProject = lazy(() => import("@/pages/tools/PageOptimizerProject"));

// Root index: marketing landing for logged-out visitors, app home when signed in
function IndexGate() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return isAuthenticated ? <HomePage /> : <LandingPage />;
}

// Protected Route wrapper - checks if route requires authentication
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Check if this route requires authentication
  const needsAuth = requiresAuth(location.pathname);
  
  // If route doesn't require auth, allow access
  if (!needsAuth) {
    return children;
  }
  
  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  return children;
}

// Individual route protection wrapper
function ProtectedTool({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Check if this specific route requires authentication
  const needsAuth = requiresAuth(location.pathname);
  
  // If route doesn't require auth, allow access
  if (!needsAuth) {
    return children;
  }
  
  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  return children;
}

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  const { checkAuth } = useAuth();
  
  // Check for auth_success in URL (OAuth callback redirect)
  if (location.search?.includes('auth_success=true')) {
    // Refresh auth state and redirect to home
    checkAuth();
    return <Navigate to="/" replace />;
  }
  
  // Check URL fragment for session_id (legacy OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/product/:slug" element={<FlagshipToolPage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public shared report pages — standalone, no layout/nav */}
      <Route path="shared/rank-tracker/:shareId" element={<SharedRankTrackerPage />} />
      <Route path="shared/:shareId" element={<UniversalSharedReport />} />
      <Route path="share/:shareId" element={<SharedReportPage />} />

      {/* All routes wrapped in Layout - individual tools check their own auth */}
      <Route path="/" element={<Layout />}>
        <Route index element={<IndexGate />} />
        <Route path="tools" element={<HomePage />} />
        {/* Resources - public */}
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="resources/:slug" element={<ArticlePage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="billing/success" element={<BillingSuccessPage />} />
        <Route path="terms" element={<TermsOfService />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="cookies" element={<CookiePolicy />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="custom-tools" element={<CustomToolsPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="getting-started" element={<OnboardingPage />} />
        <Route path="content-machine" element={<ProtectedTool><ContentMachinePage /></ProtectedTool>} />
        <Route path="production-pipeline" element={<ProtectedTool><ProductionPipelinePage /></ProtectedTool>} />
        <Route path="doc/:slug" element={<ProtectedTool><DocPage /></ProtectedTool>} />
        <Route path="production-pipeline/schedule" element={<ProtectedTool><SchedulePage /></ProtectedTool>} />
        <Route path="production-pipeline/backlog" element={<ProtectedTool><BacklogPage /></ProtectedTool>} />
        <Route path="production-pipeline/learned-phrases" element={<ProtectedTool><LearnedPhrasesPage /></ProtectedTool>} />
        <Route path="keyword-rules" element={<ProtectedTool><KeywordRulesPage /></ProtectedTool>} />
        <Route path="seo-services" element={<ProtectedTool><ServicesProposalPage /></ProtectedTool>} />
        <Route path="seo-services/builder" element={<ProtectedTool><ProposalBuilderPage /></ProtectedTool>} />
        <Route path="production-pipeline/:batchId/keywords" element={<ProtectedTool><KeywordsPage /></ProtectedTool>} />
        <Route path="production-pipeline/:batchId/editorial-qa" element={<ProtectedTool><EditorialQAPage /></ProtectedTool>} />
        <Route path="production-pipeline/:batchId/content" element={<ProtectedTool><ContentPage /></ProtectedTool>} />
        <Route path="production-pipeline/:batchId/recipe/:assignmentId" element={<ProtectedTool><RecipeDetailPage /></ProtectedTool>} />
        <Route path="production-pipeline/:batchId/images" element={<ProtectedTool><ImagesPage /></ProtectedTool>} />
        <Route path="production-pipeline/:batchId/publish" element={<ProtectedTool><PublishPage /></ProtectedTool>} />
        <Route path="recipe-prioritizer" element={<ProtectedTool><RecipePrioritizerPage /></ProtectedTool>} />
        {/* Admin routes */}
        <Route path="admin" element={<ProtectedTool><AdminDashboard /></ProtectedTool>} />
        <Route path="admin/articles/new" element={<ProtectedTool><ArticleEditor /></ProtectedTool>} />
        <Route path="admin/articles/:articleId" element={<ProtectedTool><ArticleEditor /></ProtectedTool>} />
        <Route path="tools/:categoryId" element={<ToolCategoryPage />} />
        {/* Premium tools */}
        <Route path="tools/keyword-research/keyword-density" element={<ProtectedTool><KeywordDensityTool /></ProtectedTool>} />
        <Route path="tools/keyword-research/keyword-mapping" element={<ProtectedTool><KeywordMappingTool /></ProtectedTool>} />
        <Route path="tools/keyword-research/keyword-strategy" element={<ProtectedTool><KeywordStrategyTool /></ProtectedTool>} />
                <Route path="tools/keyword-research/serp-similarity" element={<ProtectedTool><SerpSimilarityTool /></ProtectedTool>} />
                <Route path="tools/keyword-research/query-topics" element={<ProtectedTool><QueryTopicsPage /></ProtectedTool>} />
                <Route path="tools/keyword-research/keyword-clustering" element={<ProtectedTool><KeywordClusteringTool /></ProtectedTool>} />
                <Route path="tools/keyword-research/keyword-research-workflow" element={<ProtectedTool><KeywordResearchWorkflow /></ProtectedTool>} />
                <Route path="tools/keyword-research/content-gap" element={<ProtectedTool><ContentGapTool /></ProtectedTool>} />
                <Route path="tools/keyword-research/paa-cluster" element={<ProtectedTool><PAAClusterTool /></ProtectedTool>} />
                <Route path="tools/competitive-analysis/keyword-gap" element={<ProtectedTool><KeywordGapAnalysisTool /></ProtectedTool>} />
                <Route path="tools/competitive-analysis/backlink-gap" element={<ProtectedTool><BacklinkGapTool /></ProtectedTool>} />
                <Route path="tools/competitive-analysis/content-gap-pillars" element={<ProtectedTool><CompetitorContentGapTool /></ProtectedTool>} />
        <Route path="tools/on-page-optimization/internal-linking" element={<ProtectedTool><InternalLinkingTool /></ProtectedTool>} />
        <Route path="tools/content-creation/content-generator" element={<ProtectedTool><ContentGeneratorTool /></ProtectedTool>} />
        <Route path="tools/content-creation/content-analyzer" element={<ProtectedTool><ContentAnalyzerTool /></ProtectedTool>} />
        <Route path="tools/content-creation/content-brief" element={<ProtectedTool><ContentBriefTool /></ProtectedTool>} />
                <Route path="tools/content-creation/voice-profile" element={<ProtectedTool><VoiceProfileTool /></ProtectedTool>} />
                <Route path="tools/content-creation/content-hub-planner" element={<ProtectedTool><ContentHubPlanner /></ProtectedTool>} />
        <Route path="tools/seo-audit/screaming-frog-analyzer" element={<ProtectedTool><ScreamingFrogAnalyzerTool /></ProtectedTool>} />
        <Route path="tools/seo-audit/page-type-assessment" element={<ProtectedTool><PageTypeAssessment /></ProtectedTool>} />
        <Route path="tools/seo-audit/audit-index" element={<ProtectedTool><AuditIndexListPage /></ProtectedTool>} />
        <Route path="tools/seo-audit/audit-index/:projectId" element={<ProtectedTool><AuditIndexProjectPage /></ProtectedTool>} />
        <Route path="tools/data-analysis/data-insights" element={<ProtectedTool><DataInsightsPage /></ProtectedTool>} />
        <Route path="tools/keyword-tracking/rank-tracker" element={<ProtectedTool><RankTrackerTool /></ProtectedTool>} />
        <Route path="tools/keyword-tracking/position-change" element={<ProtectedTool><KeywordPositionChange /></ProtectedTool>} />
        <Route path="tools/misc-projects/branded-content" element={<ProtectedTool><BrandedContentTool /></ProtectedTool>} />
        <Route path="tools/misc-projects/category-content" element={<ProtectedTool><CategoryContentTool /></ProtectedTool>} />
        <Route path="tools/misc-projects/plp-keywords" element={<PLPKeywordTool />} />
        {/* Free tools - no protection needed */}
        <Route path="tools/on-page-optimization/meta-analyzer" element={<MetaAnalyzerTool />} />
        <Route path="tools/on-page-optimization/title-optimizer" element={<TitleTagOptimizer />} />
        <Route path="tools/on-page-optimization/heading-analyzer" element={<HeadingAnalyzerTool />} />
        <Route path="tools/on-page-optimization/keyword-cannibalization" element={<KeywordCannibalizationFinder />} />
        <Route path="tools/on-page-optimization/page-seo-analyzer" element={<PageSeoAnalyzer />} />
        <Route path="tools/on-page-optimization/page-optimizer" element={<ProtectedTool><PageOptimizer /></ProtectedTool>} />
        <Route path="tools/on-page-optimization/page-optimizer/:projectId" element={<ProtectedTool><PageOptimizerProject /></ProtectedTool>} />
        <Route path="tools/on-page-optimization/content-audit" element={<ContentAuditTool />} />
        <Route path="tools/on-page-optimization/entity-seo" element={<EntitySeoTool />} />
        <Route path="tools/misc-projects/image-optimizer" element={<ImageOptimizerTool />} />
        <Route path="tools/misc-projects/recipe-scraper" element={<ProtectedTool><RecipeScraperTool /></ProtectedTool>} />
        <Route path="tools/misc-projects/seo-director" element={<ProtectedTool><SEODirectorTool /></ProtectedTool>} />
        <Route path="tools/seo-team/seo-director" element={<ProtectedTool><SEODirectorTool /></ProtectedTool>} />
        <Route path="tools/seo-team/keyword-research-agent" element={<ProtectedTool><KeywordResearchAgentTool /></ProtectedTool>} />
        <Route path="tools/seo-team/landing-page-auditor" element={<ProtectedTool><LandingPageAuditorTool /></ProtectedTool>} />
        <Route path="tools/seo-team/website-data-collector" element={<ProtectedTool><WebsiteDataCollectorTool /></ProtectedTool>} />
        <Route path="tools/technical-seo/sitemap-validator" element={<SitemapValidatorTool />} />
        <Route path="tools/technical-seo/robots-checker" element={<RobotsCheckerTool />} />
        <Route path="tools/technical-seo/schema-validator" element={<SchemaValidatorTool />} />
        <Route path="tools/technical-seo/schema-generator" element={<SchemaGeneratorTool />} />
        <Route path="tools/technical-seo/index-checker" element={<IndexCheckerTool />} />
        <Route path="tools/technical-seo/schema-recommender" element={<SchemaRecommenderTool />} />
                <Route path="tools/technical-seo/schema-audit" element={<SchemaAuditDashboard />} />
        {/* Protected dashboards */}
        <Route path="dashboard/rank-tracker" element={<ProtectedTool><RankTrackerDashboard /></ProtectedTool>} />
        <Route path="dashboard/search-console" element={<ProtectedTool><GSCDashboard /></ProtectedTool>} />
        <Route path="results" element={<ProtectedTool><ResultsHistoryPage /></ProtectedTool>} />
        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="app-container">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="bottom-right" />
      <ConfirmDialogHost />
    </div>
  );
}

export default App;
